// Run-pinning context (admin preview). Admins can pin ANY publish run —
// including poisoned ones — and the Report + A/B Explore pages compute from
// that run's output, exactly as consumers would see it. Non-admins always
// ride the current clean run (the server enforces this regardless).

import { createContext, useContext, useEffect, useState } from "react";
import { RunsApi } from "./useApi.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import Icon from "../components/Icon.jsx";

const RunContext = createContext({ runId: null, setRunId: () => {}, runs: [], current: null, pinnedRun: null });

export function RunProvider({ children }) {
  const { user } = useAuth();
  const [runId, setRunId] = useState(null);     // null = live (current clean)
  const [runs, setRuns] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (!user?.role) return;
    RunsApi.current().then(setCurrent).catch(() => setCurrent(null));
    // Every role fetches the run list — the SERVER filters it: admins get all
    // runs (incl. poisoned, for diagnostics); clients/tenants get only CLEAN
    // published runs within their scope, so the A/B snapshot pickers can never
    // expose a poisoned/unreliable run to them.
    RunsApi.list().then(setRuns).catch(() => setRuns([]));
  }, [user]);

  const pinnedRun = runId ? runs.find((r) => r.id === runId) ?? null : null;

  return (
    <RunContext.Provider value={{ runId, setRunId, runs, current, pinnedRun }}>
      {children}
    </RunContext.Provider>
  );
}

export const useRun = () => useContext(RunContext);

/** Admin-only dropdown to pin a run for preview (amber when pinned). */
export function RunPicker() {
  const { user } = useAuth();
  const { runId, setRunId, runs, current } = useRun();
  if (user?.role !== "admin" || runs.length === 0) return null;
  return (
    <div className={`run-select-wrap${runId ? " pinned" : ""}`}
      title="Preview the Report & A/B calculator against any snapshot's output">
      <Icon name="pin" size={13} />
      <select
        className="run-select"
        value={runId ?? ""}
        onChange={(e) => setRunId(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">{current ? `Snapshot #${current.id} · live` : "Live (current clean)"}</option>
        {runs.map((r) => (
          <option key={r.id} value={r.id}>
            #{r.id} · {r.sourceKey} · {r.verdict}{r.verdict === "poisoned" ? " ⚠" : ""}
            {r.periodMin ? ` · ${r.periodMin}→${r.periodMax}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Banner shown whenever a run is pinned (especially poisoned ones). */
export function RunBanner() {
  const { pinnedRun, setRunId } = useRun();
  if (!pinnedRun) return null;
  const poisoned = pinnedRun.verdict === "poisoned";
  const reasons = (() => { try { return JSON.parse(pinnedRun.reasonsJson || "[]"); } catch { return []; } })();
  return (
    <div className="intro-wrap">
      <div className={`run-banner${poisoned ? " poisoned" : ""}`}>
        <Icon name={poisoned ? "alert" : "eye"} size={15} />
        <span>
          <b>PREVIEW</b> — snapshot #{pinnedRun.id} ({pinnedRun.sourceKey}, {pinnedRun.verdict})
          {reasons.length > 0 && <> · {reasons.join(" · ")}</>}. Report &amp; Explore reflect this snapshot.
        </span>
        <button type="button" className="btn ghost sm" onClick={() => setRunId(null)}>
          <Icon name="refresh" size={13} /> Back to live
        </button>
      </div>
    </div>
  );
}
